import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { Profile, Plan } from '../types';
import { useAuthContext } from '../contexts/AuthContext';

export const useProfile = () => {
    const { user } = useAuthContext();
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = useCallback(async () => {
        if (!user) {
            setProfile(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        const { data, error: fetchError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle();

        if (fetchError) {
            setError(fetchError.message);
        } else {
            setProfile(data as Profile);
        }
        setLoading(false);
    }, [user]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    // Supabase Realtime — plan updates instantly when webhook fires
    useEffect(() => {
        if (!user) return;

        const channel = supabase
            .channel('profile-plan-changes')
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'profiles',
                    filter: `id=eq.${user.id}`,
                },
                (payload) => {
                    setProfile((prev) =>
                        prev ? { ...prev, ...(payload.new as Profile) } : (payload.new as Profile)
                    );
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user]);

    const updateProfile = async (updates: Partial<Profile>) => {
        if (!user) return { success: false };
        const { error: updateError } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', user.id);

        if (updateError) {
            setError(updateError.message);
            return { success: false };
        }
        await fetchProfile();
        return { success: true };
    };

    // Derived state for plan and expiry
    const { plan, expiresAt, isPremium, isCreator, isPro, isLifetime, isFree, daysRemaining } = useMemo(() => {
        let currentPlan: Plan = profile?.plan || 'free';
        let expiryDate: Date | null = profile?.plan_expires_at ? new Date(profile.plan_expires_at) : null;

        // Local expiry check — skip for lifetime (lifetime has no expiry)
        if (currentPlan !== 'lifetime' && expiryDate && currentPlan !== 'free') {
            const now = new Date();
            if (now > expiryDate) {
                currentPlan = 'free';
            }
        }

        const days = expiryDate
            ? Math.ceil((expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
            : null;

        return {
            plan: currentPlan,
            expiresAt: expiryDate,
            isPremium: currentPlan === 'creator' || currentPlan === 'pro' || currentPlan === 'lifetime',
            isCreator: currentPlan === 'creator',
            isPro: currentPlan === 'pro',
            isLifetime: currentPlan === 'lifetime',
            isFree: currentPlan === 'free',
            daysRemaining: currentPlan === 'lifetime' ? null : days
        };
    }, [profile]);

    return {
        profile,
        loading,
        error,
        fetchProfile,
        updateProfile,
        plan,
        isPremium,
        isCreator,
        isPro,
        isLifetime,
        isFree,
        expiresAt,
        daysRemaining
    };
};
